// @flow
import type { Action } from 'app/common/redux/types';
import type { BoardActivityItem } from 'app/common/redux/reducers/activityItems';
import type { CarouselPinData, DetailedPinUser } from 'app/packages/pinterest-flow-types/pin';

export type SaveTarget = {|
  boardId: string,
  localPinId?: string,
  sectionId?: string,
  title: string,
  url?: string,
|};

export type State = {
  access?: Array<string>,
  aggregated_pin_data?: {
    comment_count: number,
    id: string,
    did_it_data: {
      images_count: number,
      videos_count: number,
    },
  },
  board?: { id: string },
  carousel_data?: CarouselPinData,
  created_at?: string,
  board_activity?: ?BoardActivityItem,
  domain?: string,
  dominant_color?: string,
  done_by_me?: boolean,
  feedbackText?: {
    title: string,
    subTitle?: string,
  },
  grid_description?: string,
  id: string,
  images?: { [size: string]: { url: string, width: number, height: number } },
  is_promoted?: Boolean,
  is_repin?: boolean,
  is_quick_promotable?: boolean,
  pinner?: DetailedPinUser,
  savedInfo?: SaveTarget,
  showFeedback?: boolean,
  showUndo?: boolean,
  tracking_params?: string,
  tracking_params_map?: { [key: string]: string },
  type: 'pin',
  saved?: boolean,
};

export type ImageDimensions = {
  height: number,
  width: number,
};

export type SummaryPin = {
  cacheable_id: string,
  comment_count: number,
  created_at: string,
  description: string,
  domain: string,
  id: string,
  image_large_size_pixels: ImageDimensions,
  image_large_size_points: ImageDimensions,
  image_large_url: string,
  image_medium_size_pixels: ImageDimensions,
  image_medium_size_points: ImageDimensions,
  image_medium_url: string,
  image_square_size_pixels: ImageDimensions,
  image_square_size_points: ImageDimensions,
  image_square_url: string,
  is_downstream_promotion: boolean,
  is_playable: boolean,
  is_repin: boolean,
  is_uploaded: boolean,
  is_video: boolean,
  link: string,
  price_currency: string,
  price_value: number,
  promoter: ?string,
  repin_count: number,
  title: string,
  tracked_link: string,
  type: string,
};

export default function Pin(state: State, action: Action): State {
  if (action.type === 'PIN_DID_IT') {
    const { id, value } = action.payload;
    if (id === state.id) {
      const increment = value ? 1 : -1;
      const didItData = (state.aggregated_pin_data || {}).did_it_data;

      const updatedDidItData = {
        ...didItData,
        images_count: (didItData.images_count || 0) + increment, //TODO(danay): should perhaps distinguish between photo and video)
      };

      const updatedAggregatedPinData = {
        ...state.aggregated_pin_data,
        did_it_data: updatedDidItData,
      };

      return {
        ...state,
        aggregated_pin_data: updatedAggregatedPinData,
        done_by_me: value,
      };
    }
  } else if (action.type === 'PIN_UPDATE_COMMENT_COUNT') {
    const { id, increment } = action.payload;
    if (id === state.id && state.aggregated_pin_data) {
      const updatedAggregatedPinData = {
        ...state.aggregated_pin_data,
        comment_count: state.aggregated_pin_data.comment_count + increment,
      };
      return {
        ...state,
        aggregated_pin_data: updatedAggregatedPinData,
      };
    }
  } else if (action.type === 'PIN_SHOW_FEEDBACK') {
    const { id, value, text } = action.payload;
    if (id === state.id) {
      return {
        ...state,
        showFeedback: value,
        feedbackText: text,
      };
    }
  } else if (action.type === 'PIN_SHOW_UNDO_AND_FEEDBACK') {
    const { id, value, text, undoAction, undoText } = action.payload;
    if (id === state.id) {
      return {
        ...state,
        showFeedback: value,
        feedbackText: text,
        undoAction,
        undoText,
      };
    }
  } else if (action.type === 'PIN_SHOW_FEEDBACK_OVERLAY') {
    const { id, value } = action.payload;
    if (id === state.id) {
      return {
        ...state,
        showFeedbackOverlay: value,
      };
    }
  } else if (action.type === 'PIN_HIDE_FEEDBACK') {
    const { id, value } = action.payload;
    if (id === state.id) {
      return {
        ...state,
        showFeedback: value,
      };
    }
  } else if (action.type === 'FEEDBACK_WINDOW_SUBMIT') {
    const { pinId, text } = action.payload;
    if (pinId === state.id) {
      return {
        ...state,
        sentFeedback: text,
      };
    }
  } else if (action.type === 'PIN_BETTER_SAVE') {
    const { pinId, boardId, localPinId, sectionId, title, url } = action.payload;
    if (pinId === state.id) {
      return {
        ...state,
        savedInfo: {
          boardId,
          localPinId,
          sectionId,
          title,
          url,
        },
      };
    }
  } else if (action.type === 'PIN_BETTER_UNSAVE') {
    const { pinId } = action.payload;
    if (pinId === state.id) {
      const updatedPin = { ...state };
      delete updatedPin.savedInfo;
      return {
        ...updatedPin,
      };
    }
  } else if (action.type === 'PIN_BETTER_SELECT_BOARD_OR_SECTION') {
    const { pinId, boardId, sectionId, title, url } = action.payload;
    if (pinId === state.id) {
      return {
        ...state,
        selectedItem: {
          boardId,
          sectionId,
          title,
          url,
        },
      };
    }
  } else if (action.type === 'PIN_SAVE') {
    const { pinId, saved } = action.payload;
    if (pinId === state.id) {
      return {
        ...state,
        saved,
      };
    }
  } else if (action.type === 'CHANGE_CAROUSEL_SLOT_INDEX') {
    const { pinId, index } = action.payload;
    if (pinId === state.id) {
      return {
        ...state,
        carousel_data: {
          ...state.carousel_data,
          index,
        },
      };
    }
  }

  return state;
}
